import { useCallback, useEffect, useRef, useState } from "react";
import { PsnsRedriveApiFactory } from "../../../lusca-search-api/LuscaSearchApiFactory";
import {
  PsnsRedriveSummary,
  Stage,
} from "../../../lusca-search-api/generated-src";
import { getApiErrorMessage } from "../apiErrorMessage";

// The stage's most recent PSNS redrives. Loads while `enabled` (the list is
// on screen), so returning from an execution's view shows the latest runs.
export const usePsnsRedrives = (stage: Stage, enabled: boolean) => {
  const [redrives, setRedrives] = useState<PsnsRedriveSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Only the latest request may update the list, so a slow response for the
  // previous stage can't overwrite this stage's runs.
  const latestRequest = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError("");
    try {
      const response = await PsnsRedriveApiFactory().getPsnsRedrivesInfo(stage);
      if (request === latestRequest.current) {
        setRedrives(Array.isArray(response.data) ? response.data : []);
      }
    } catch (fetchError) {
      if (request === latestRequest.current) {
        setRedrives([]);
        setError(
          `Failed to load recent PSNS redrives: ${getApiErrorMessage(
            fetchError
          )}`
        );
      }
    } finally {
      if (request === latestRequest.current) {
        setLoading(false);
      }
    }
  }, [stage]);

  useEffect(() => {
    if (enabled) {
      refresh().catch(() => undefined);
    }
  }, [enabled, refresh]);

  // Unmounting makes any in-flight response stale, so it's dropped.
  useEffect(
    () => () => {
      latestRequest.current += 1;
    },
    []
  );

  return { redrives, loading, error, refresh };
};
